import React from 'react';
import { useTheme as useEmotionTheme } from '@emotion/react';
import { MoonFilled, MoonOutlined, SunFilled, SunOutlined } from '@ant-design/icons';
import Tooltip from 'components/common/tooltip';
import { BridgedButton as Button } from 'components/common/bridged-button';
import { IconButton } from 'components/common/icon-button';
import { useTheme } from 'hooks/use-theme';

/**
 * Forked rather than routed through `IconButton` on both sides. `IconButton` already forks its own DOM
 * — a real `<button>` for the new design, a bare `<span>` for the legacy one — so handing it the whole
 * control swapped switch-off from a text Button to that span, which carries `right: 20px; top: 20px;
 * margin-right: 10px` and a 1.2x hover scale. The parity gate caught it.
 *
 * The Filled icons stay on the legacy branch for the same reason: an outlined glyph is a visible change
 * and switch-off has to be pixel-identical (D7.7). That is why this file keeps its Filled-icon lint
 * allowlist entry — the debt is real and APO-484 pays it off with the branch.
 */
export default function AppThemeSwitchButton() {
  const { isDarkTheme, setAppTheme } = useTheme();
  const { isNewDesign } = useEmotionTheme();
  const toggle = () => setAppTheme(isDarkTheme ? 'light' : 'dark');

  if (isNewDesign) {
    return (
      <IconButton
        tooltipTitleId="toggle.theme"
        dataE2e="theme-switch-button"
        ariaLabel="Toggle theme"
        icon={isDarkTheme ? MoonOutlined : SunOutlined}
        // Figma's Icon button in Navigation/Top is the Small variant.
        size="small"
        onClick={toggle}
      />
    );
  }

  return (
    <Tooltip placement="left" titleId="toggle.theme">
      <Button type="text" onClick={toggle}>
        {isDarkTheme ? <MoonFilled /> : <SunFilled />}
      </Button>
    </Tooltip>
  );
}
